import { Component, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import Swal from 'sweetalert2';
import { NotificationService } from '../../../../services/notifications';


@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss']
})
export class NotificationsComponent implements OnInit {
  desktopPreview: string | null = null;
  mobilePreview: string | null = null;

  allData: any[] = [];
  displayedColumns: string[] = [
    'notification_head_ar',
    'notification_head_en',
    'notification_body_ar',
    'notification_body_en',
    'link',
    'actions'
  ];
  dataSource: MatTableDataSource<any>;
  contentReady: boolean = true;
  noData: boolean = false;
  isAr: boolean = localStorage.getItem('language') === 'ar';
  notificationForm!: FormGroup;
  selectedImage: File | null = null;
  selectedMobileImage: File | null = null;

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor(
    private notificationService: NotificationService,
    private fb: FormBuilder
  ) {
    this.dataSource = new MatTableDataSource(this.allData);
  }

  ngOnInit(): void {
    this.loadNotifications();
    this.initForm();
  }

  initForm() {
    this.notificationForm = this.fb.group({
      notification_head_ar: ['', Validators.required],
      notification_head_en: ['', Validators.required],
      notification_body_ar: [''],
      notification_body_en: [''],
      link: [''],
      image: [null],
      mobile_image: [null]
    });
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (!file) return;

    const maxSize = 2 * 1024 * 1024; // 2MB

    if (file.size > maxSize) {
      Swal.fire({
        icon: 'error',
        title: this.isAr ? 'حجم الصورة كبير' : 'Image too large',
        text: this.isAr
          ? 'حجم الصورة يجب ألا يزيد عن 2 ميجابايت'
          : 'Image must be smaller than 2MB',
        confirmButtonText: this.isAr ? 'تمام' : 'OK'
      });

      event.target.value = '';
      this.selectedImage = null;
      this.desktopPreview = null;
      return;
    }

    this.selectedImage = file;
    this.notificationForm.patchValue({ image: file });

    const reader = new FileReader();
    reader.onload = () => {
      this.desktopPreview = reader.result as string;
    };
    reader.readAsDataURL(file);
  }

  onMobileImageSelected(event: any) {
    const file = event.target.files[0];
    if (!file) return;

    const maxSize = 2 * 1024 * 1024; // 2MB

    if (file.size > maxSize) {
      Swal.fire({
        icon: 'error',
        title: this.isAr ? 'حجم الصورة كبير' : 'Image too large',
        text: this.isAr
          ? 'حجم الصورة يجب ألا يزيد عن 2 ميجابايت'
          : 'Image must be smaller than 2MB',
        confirmButtonText: this.isAr ? 'تمام' : 'OK'
      });

      event.target.value = '';
      this.selectedMobileImage = null;
      this.mobilePreview = null;
      return;
    }

    this.selectedMobileImage = file;
    this.notificationForm.patchValue({ mobile_image: file });

    const reader = new FileReader();
    reader.onload = () => {
      this.mobilePreview = reader.result as string;
    };
    reader.readAsDataURL(file);
  }

  loadNotifications(): void {
    this.contentReady = true;
    this.notificationService.getAllNotifications().subscribe({
      next: (data) => {
        this.allData = data;
        this.dataSource = new MatTableDataSource(this.allData);
        this.dataSource.paginator = this.paginator;
        this.contentReady = false;
        this.noData = this.allData.length === 0;
      },
      error: (err) => {
        console.error(err);
        this.contentReady = false;
      },
    });
  }

  addNotification(): void {
       this.contentReady = true;
    if (this.notificationForm.invalid) return;

    const formData = new FormData();
    formData.append('notification_head_ar', this.notificationForm.value.notification_head_ar);
    formData.append('notification_head_en', this.notificationForm.value.notification_head_en);
    formData.append('notification_body_ar', this.notificationForm.value.notification_body_ar || '');
    formData.append('notification_body_en', this.notificationForm.value.notification_body_en || '');
    formData.append('link', this.notificationForm.value.link || '');

    if (this.selectedImage) {
      formData.append('image', this.selectedImage);
    }

    if (this.selectedMobileImage) {
      formData.append('mobile_image', this.selectedMobileImage);
    }

    this.notificationService.addNotification(formData).subscribe({
      next: (res) => {
        this.notificationForm.reset();
        this.selectedImage = null;
        this.selectedMobileImage = null;
        this.desktopPreview = null;
        this.mobilePreview = null;
           this.contentReady = false;
        this.loadNotifications();
        Swal.fire({
          icon: 'success',
          title: 'تم الحفظ بنجاح',
          text: 'تمت إضافة الإشعار بنجاح!',
          confirmButtonText: 'موافق'
        });
      },
      error: (err) => {
         this.contentReady = false;
        const backendMessage =
          err?.error?.errors?.image?.[0] ||
          err?.error?.errors?.mobile_image?.[0] ||
          err?.error?.message ||
          'تعذر إضافة الإشعار، حاول مرة أخرى.';

        Swal.fire({
          icon: 'error',
          title: 'حدث خطأ',
          text: this.isAr
            ? this.translateBackendError(backendMessage)
            : backendMessage
        });
      }
    });
  }

  deleteItem(id: number): void {
    Swal.fire({
      title: 'هل أنت متأكد؟',
      text: 'لن يمكنك التراجع بعد الحذف!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'نعم، احذف!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.notificationService.deleteNotification(id).subscribe({
          next: () => {
            this.loadNotifications();
            Swal.fire('تم الحذف!', 'تم حذف الإشعار بنجاح.', 'success');
          },
          error: (err) => console.error(err),
        });
      }
    });
  }

  translateBackendError(message: string): string {
    if (!message) return 'حدث خطأ غير متوقع';

    if (message.includes('The image field must not be greater than 2048 kilobytes')) {
      return 'حجم صورة الديسكتوب أكبر من 2 ميجابايت';
    }

    if (message.includes('The mobile image field must not be greater than 2048 kilobytes')) {
      return 'حجم صورة الموبايل أكبر من 2 ميجابايت';
    }

    return message;
  }
}
